import React, { useState } from 'react';
import { Note } from '../../types';
import {
  Pin,
  FileText,
  MoreVertical,
  Edit3,
  Copy,
  Trash2,
  Check,
  Plus,
  Lock,
  Unlock,
  KeyRound,
  Calendar,
  CheckSquare,
  Square,
} from 'lucide-react';

interface NoteCardProps {
  note: Note;
  viewMode?: 'grid' | 'list';
  isSelected?: boolean;
  isSelectionMode?: boolean;
  onToggleSelect?: (id: string) => void;
  onEdit: (note: Note) => void;
  onDelete: (id: string) => void;
  onTogglePin: (id: string) => void;
  onDuplicate: (id: string) => void;
  onLock: (note: Note) => void;
  onUnlock: (note: Note) => void;
  onRemoveLock: (note: Note) => void;
  onAddReminder?: (note: Note) => void;
}

export const NoteCard: React.FC<NoteCardProps> = ({
  note,
  viewMode = 'grid',
  isSelected = false,
  isSelectionMode = false,
  onToggleSelect,
  onEdit,
  onDelete,
  onTogglePin,
  onDuplicate,
  onLock,
  onUnlock,
  onRemoveLock,
  onAddReminder,
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCardClick = (e: React.MouseEvent) => {
    if (isSelectionMode && onToggleSelect) {
      e.stopPropagation();
      onToggleSelect(note.id);
      return;
    }
    if (note.isLocked) {
      onUnlock(note);
    } else {
      onEdit(note);
    }
  };

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (note.isLocked) {
      onUnlock(note);
      return;
    }
    const text = `${note.title}\n\n${note.content || ''}`;
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
    setShowMenu(false);
  };

  // Close menu on outside click
  React.useEffect(() => {
    if (!showMenu) return;
    const handleGlobalClick = () => setShowMenu(false);
    window.addEventListener('click', handleGlobalClick);
    return () => window.removeEventListener('click', handleGlobalClick);
  }, [showMenu]);

  // List View Mode
  if (viewMode === 'list') {
    return (
      <div
        onClick={handleCardClick}
        className={`group bg-white dark:bg-[#111827] border transition-all duration-200 cursor-pointer shadow-xs hover:shadow-md rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 ${
          isSelected
            ? 'border-purple-500 ring-2 ring-purple-500/20 bg-purple-500/[0.04]'
            : note.isLocked
            ? 'border-amber-500/30 dark:border-amber-500/25 hover:border-amber-500/60 bg-amber-500/[0.02]'
            : 'border-slate-200/80 dark:border-slate-800 hover:border-purple-500/50 dark:hover:border-purple-500/50'
        }`}
      >
        <div className="flex items-center gap-3 min-w-0 flex-1">
          {/* Selection Checkbox */}
          {(isSelectionMode || onToggleSelect) && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onToggleSelect && onToggleSelect(note.id);
              }}
              className={`p-1 rounded-lg transition-colors flex-shrink-0 ${
                isSelected
                  ? 'text-purple-600 dark:text-purple-400'
                  : 'text-slate-300 dark:text-slate-600 hover:text-slate-500'
              }`}
            >
              {isSelected ? (
                <CheckSquare className="w-4 h-4 fill-purple-500/10" />
              ) : (
                <Square className="w-4 h-4" />
              )}
            </button>
          )}

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <div
                className={`w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  note.isLocked
                    ? 'bg-amber-500/15 text-amber-600 dark:text-amber-400'
                    : 'bg-purple-500/10 text-purple-500'
                }`}
              >
                {note.isLocked ? (
                  <Lock className="w-3.5 h-3.5" />
                ) : (
                  <FileText className="w-3.5 h-3.5" />
                )}
              </div>

              <h3 className="text-sm font-bold text-slate-900 dark:text-white truncate">
                {note.title}
              </h3>

              {note.isLocked && (
                <span className="flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-md bg-amber-500/15 text-amber-600 dark:text-amber-400 border border-amber-500/20">
                  <Lock className="w-2.5 h-2.5" />
                  Locked
                </span>
              )}

              {note.pinned && (
                <span className="flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-md bg-purple-500/15 text-purple-600 dark:text-purple-400">
                  <Pin className="w-2.5 h-2.5 fill-purple-500" />
                  Pinned
                </span>
              )}

              {note.reminder && (
                <span
                  onClick={(e) => {
                    e.stopPropagation();
                    onAddReminder && onAddReminder(note);
                  }}
                  className="flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-md bg-blue-500/15 text-blue-600 dark:text-blue-400 border border-blue-500/20 hover:bg-blue-500/25"
                  title="Calendar Reminder Date"
                >
                  <Calendar className="w-2.5 h-2.5" />
                  {note.reminder}
                </span>
              )}
            </div>

            <p className="text-xs text-slate-500 dark:text-slate-400 line-clamp-1 pl-8">
              {note.isLocked ? (
                <span className="font-mono tracking-widest text-slate-400">
                  •••••••••••••••••••• (Protected by PIN)
                </span>
              ) : note.content ? (
                note.content.replace(/[#*`_]/g, '')
              ) : (
                'Empty note'
              )}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 self-end sm:self-center flex-shrink-0">
          <span className="text-[11px] text-slate-400 font-mono">
            {note.updatedAt ? new Date(note.updatedAt).toLocaleDateString() : ''}
          </span>

          {/* Quick Calendar Reminder Button */}
          {onAddReminder && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onAddReminder(note);
              }}
              className={`p-1.5 rounded-xl transition-colors ${
                note.reminder
                  ? 'text-blue-500 bg-blue-500/15 hover:bg-blue-500/25'
                  : 'text-slate-400 hover:text-blue-500 hover:bg-slate-100 dark:hover:bg-slate-800'
              }`}
              title={note.reminder ? `Reminder: ${note.reminder}` : 'Add to Calendar'}
            >
              <Calendar className="w-3.5 h-3.5" />
            </button>
          )}

          {/* Quick Lock / Unlock Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (note.isLocked) {
                onUnlock(note);
              } else {
                onLock(note);
              }
            }}
            className={`p-1.5 rounded-xl transition-colors ${
              note.isLocked
                ? 'text-amber-500 bg-amber-500/15 hover:bg-amber-500/25'
                : 'text-slate-400 hover:text-purple-500 hover:bg-slate-100 dark:hover:bg-slate-800'
            }`}
            title={note.isLocked ? 'Unlock Note' : 'Lock Note with PIN'}
          >
            {note.isLocked ? <Lock className="w-3.5 h-3.5" /> : <KeyRound className="w-3.5 h-3.5" />}
          </button>

          {/* Pin Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onTogglePin(note.id);
            }}
            className={`p-1.5 rounded-xl transition-colors ${
              note.pinned
                ? 'text-amber-500 bg-amber-500/10'
                : 'text-slate-400 hover:text-amber-500 hover:bg-slate-100 dark:hover:bg-slate-800'
            }`}
            title={note.pinned ? 'Unpin' : 'Pin'}
          >
            <Pin className={`w-3.5 h-3.5 ${note.pinned ? 'fill-amber-500' : ''}`} />
          </button>

          {/* Menu Button */}
          <div className="relative">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowMenu(!showMenu);
              }}
              className="p-1.5 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            >
              <MoreVertical className="w-3.5 h-3.5" />
            </button>

            {showMenu && (
              <div
                onClick={(e) => e.stopPropagation()}
                className="absolute right-0 top-8 z-30 w-52 bg-white dark:bg-[#1f2937] rounded-2xl shadow-xl border border-slate-200 dark:border-slate-700 py-1.5 text-xs animate-scale-in"
              >
                {note.isLocked ? (
                  <>
                    <button
                      onClick={() => {
                        onUnlock(note);
                        setShowMenu(false);
                      }}
                      className="w-full px-3 py-2 text-left font-semibold text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-950/40 flex items-center gap-2"
                    >
                      <Unlock className="w-3.5 h-3.5" />
                      <span>Unlock with PIN</span>
                    </button>

                    <button
                      onClick={() => {
                        onRemoveLock(note);
                        setShowMenu(false);
                      }}
                      className="w-full px-3 py-2 text-left font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center gap-2"
                    >
                      <KeyRound className="w-3.5 h-3.5 text-rose-500" />
                      <span>Remove Lock</span>
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => {
                        onEdit(note);
                        setShowMenu(false);
                      }}
                      className="w-full px-3 py-2 text-left font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center gap-2"
                    >
                      <Edit3 className="w-3.5 h-3.5 text-purple-500" />
                      <span>Edit Note</span>
                    </button>

                    {onAddReminder && (
                      <button
                        onClick={() => {
                          onAddReminder(note);
                          setShowMenu(false);
                        }}
                        className="w-full px-3 py-2 text-left font-semibold text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950/40 flex items-center gap-2"
                      >
                        <Calendar className="w-3.5 h-3.5" />
                        <span>{note.reminder ? 'Edit Reminder' : 'Add to Calendar'}</span>
                      </button>
                    )}

                    <button
                      onClick={() => {
                        onLock(note);
                        setShowMenu(false);
                      }}
                      className="w-full px-3 py-2 text-left font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center gap-2"
                    >
                      <Lock className="w-3.5 h-3.5 text-amber-500" />
                      <span>Lock with PIN</span>
                    </button>

                    <button
                      onClick={handleCopy}
                      className="w-full px-3 py-2 text-left font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center gap-2"
                    >
                      {copied ? (
                        <Check className="w-3.5 h-3.5 text-emerald-500" />
                      ) : (
                        <Copy className="w-3.5 h-3.5 text-blue-500" />
                      )}
                      <span>{copied ? 'Copied!' : 'Copy Content'}</span>
                    </button>

                    <button
                      onClick={() => {
                        onDuplicate(note.id);
                        setShowMenu(false);
                      }}
                      className="w-full px-3 py-2 text-left font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center gap-2"
                    >
                      <Plus className="w-3.5 h-3.5 text-emerald-500" />
                      <span>Duplicate</span>
                    </button>
                  </>
                )}

                <div className="h-px bg-slate-100 dark:bg-slate-800 my-1" />

                <button
                  onClick={() => {
                    onDelete(note.id);
                    setShowMenu(false);
                  }}
                  className="w-full px-3 py-2 text-left font-semibold text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/40 flex items-center gap-2"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  <span>Move to Trash</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Grid Box View Mode (Simple Box)
  return (
    <div
      onClick={handleCardClick}
      className={`group bg-white dark:bg-[#111827] border transition-all duration-200 cursor-pointer shadow-xs hover:shadow-xl hover:-translate-y-1 rounded-3xl p-5 flex flex-col justify-between min-h-[170px] relative ${
        isSelected
          ? 'border-purple-500 ring-2 ring-purple-500/20 bg-purple-500/[0.03]'
          : note.isLocked
          ? 'border-amber-500/30 dark:border-amber-500/25 hover:border-amber-500/60 bg-amber-500/[0.02]'
          : 'border-slate-200/80 dark:border-slate-800 hover:border-purple-500/50 dark:hover:border-purple-500/50'
      }`}
    >
      <div>
        {/* Card Header: Title, Badges & Actions */}
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex items-start gap-2.5 flex-1 min-w-0">
            {/* Selection Checkbox */}
            {(isSelectionMode || onToggleSelect) && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleSelect && onToggleSelect(note.id);
                }}
                className={`mt-0.5 p-0.5 rounded-lg transition-colors flex-shrink-0 ${
                  isSelected
                    ? 'text-purple-600 dark:text-purple-400'
                    : 'text-slate-300 dark:text-slate-600 hover:text-slate-500'
                }`}
              >
                {isSelected ? (
                  <CheckSquare className="w-4 h-4 fill-purple-500/10" />
                ) : (
                  <Square className="w-4 h-4" />
                )}
              </button>
            )}

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 mb-1.5 flex-wrap">
                {note.isLocked && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-md bg-amber-500/15 text-amber-600 dark:text-amber-400 border border-amber-500/20">
                    <Lock className="w-2.5 h-2.5" />
                    Locked
                  </span>
                )}

                {note.pinned && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-md bg-purple-500/15 text-purple-600 dark:text-purple-400">
                    <Pin className="w-2.5 h-2.5 fill-purple-500" />
                    Pinned
                  </span>
                )}

                {note.reminder && (
                  <span
                    onClick={(e) => {
                      e.stopPropagation();
                      onAddReminder && onAddReminder(note);
                    }}
                    className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-md bg-blue-500/15 text-blue-600 dark:text-blue-400 border border-blue-500/20 hover:bg-blue-500/25"
                    title="Calendar Reminder Date"
                  >
                    <Calendar className="w-2.5 h-2.5" />
                    {note.reminder}
                  </span>
                )}
              </div>

              <h3 className="text-base font-bold text-slate-900 dark:text-white leading-snug break-words">
                {note.title}
              </h3>
            </div>
          </div>

          <div className="flex items-center gap-1 flex-shrink-0">
            {/* Quick Calendar Reminder Button */}
            {onAddReminder && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onAddReminder(note);
                }}
                className={`p-1.5 rounded-xl transition-colors ${
                  note.reminder
                    ? 'text-blue-500 bg-blue-500/15 hover:bg-blue-500/25'
                    : 'text-slate-400 hover:text-blue-500 hover:bg-slate-100 dark:hover:bg-slate-800'
                }`}
                title={note.reminder ? `Reminder: ${note.reminder}` : 'Add to Calendar'}
              >
                <Calendar className="w-3.5 h-3.5" />
              </button>
            )}

            {/* Quick Lock/Unlock Button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (note.isLocked) {
                  onUnlock(note);
                } else {
                  onLock(note);
                }
              }}
              className={`p-1.5 rounded-xl transition-colors ${
                note.isLocked
                  ? 'text-amber-500 bg-amber-500/15 hover:bg-amber-500/25'
                  : 'text-slate-400 hover:text-purple-500 hover:bg-slate-100 dark:hover:bg-slate-800'
              }`}
              title={note.isLocked ? 'Unlock Note' : 'Lock Note with PIN'}
            >
              {note.isLocked ? <Lock className="w-3.5 h-3.5" /> : <KeyRound className="w-3.5 h-3.5" />}
            </button>

            {/* Pin Button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onTogglePin(note.id);
              }}
              className={`p-1.5 rounded-xl transition-colors ${
                note.pinned
                  ? 'text-amber-500 bg-amber-500/15'
                  : 'text-slate-400 hover:text-amber-500 hover:bg-slate-100 dark:hover:bg-slate-800'
              }`}
              title={note.pinned ? 'Unpin' : 'Pin'}
            >
              <Pin className={`w-3.5 h-3.5 ${note.pinned ? 'fill-amber-500' : ''}`} />
            </button>

            {/* Menu Dropdown */}
            <div className="relative">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowMenu(!showMenu);
                }}
                className="p-1.5 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              >
                <MoreVertical className="w-3.5 h-3.5" />
              </button>

              {showMenu && (
                <div
                  onClick={(e) => e.stopPropagation()}
                  className="absolute right-0 top-8 z-30 w-52 bg-white dark:bg-[#1f2937] rounded-2xl shadow-xl border border-slate-200 dark:border-slate-700 py-1.5 text-xs animate-scale-in"
                >
                  {note.isLocked ? (
                    <>
                      <button
                        onClick={() => {
                          onUnlock(note);
                          setShowMenu(false);
                        }}
                        className="w-full px-3 py-2 text-left font-semibold text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-950/40 flex items-center gap-2"
                      >
                        <Unlock className="w-3.5 h-3.5" />
                        <span>Unlock with PIN</span>
                      </button>

                      <button
                        onClick={() => {
                          onRemoveLock(note);
                          setShowMenu(false);
                        }}
                        className="w-full px-3 py-2 text-left font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center gap-2"
                      >
                        <KeyRound className="w-3.5 h-3.5 text-rose-500" />
                        <span>Remove Lock</span>
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => {
                          onEdit(note);
                          setShowMenu(false);
                        }}
                        className="w-full px-3 py-2 text-left font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center gap-2"
                      >
                        <Edit3 className="w-3.5 h-3.5 text-purple-500" />
                        <span>Edit Note</span>
                      </button>

                      {onAddReminder && (
                        <button
                          onClick={() => {
                            onAddReminder(note);
                            setShowMenu(false);
                          }}
                          className="w-full px-3 py-2 text-left font-semibold text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950/40 flex items-center gap-2"
                        >
                          <Calendar className="w-3.5 h-3.5" />
                          <span>{note.reminder ? 'Edit Reminder' : 'Add to Calendar'}</span>
                        </button>
                      )}

                      <button
                        onClick={() => {
                          onLock(note);
                          setShowMenu(false);
                        }}
                        className="w-full px-3 py-2 text-left font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center gap-2"
                      >
                        <Lock className="w-3.5 h-3.5 text-amber-500" />
                        <span>Lock with PIN</span>
                      </button>

                      <button
                        onClick={handleCopy}
                        className="w-full px-3 py-2 text-left font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center gap-2"
                      >
                        {copied ? (
                          <Check className="w-3.5 h-3.5 text-emerald-500" />
                        ) : (
                          <Copy className="w-3.5 h-3.5 text-blue-500" />
                        )}
                        <span>{copied ? 'Copied!' : 'Copy Content'}</span>
                      </button>

                      <button
                        onClick={() => {
                          onDuplicate(note.id);
                          setShowMenu(false);
                        }}
                        className="w-full px-3 py-2 text-left font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center gap-2"
                      >
                        <Plus className="w-3.5 h-3.5 text-emerald-500" />
                        <span>Duplicate</span>
                      </button>
                    </>
                  )}

                  <div className="h-px bg-slate-100 dark:bg-slate-800 my-1" />

                  <button
                    onClick={() => {
                      onDelete(note.id);
                      setShowMenu(false);
                    }}
                    className="w-full px-3 py-2 text-left font-semibold text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/40 flex items-center gap-2"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    <span>Move to Trash</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Note Body */}
        <div className="my-2">
          {note.isLocked ? (
            <div className="py-4 px-3 rounded-2xl bg-amber-500/5 dark:bg-amber-500/10 border border-amber-500/20 text-center space-y-2">
              <div className="w-8 h-8 rounded-xl bg-amber-500/15 text-amber-600 dark:text-amber-400 flex items-center justify-center mx-auto">
                <Lock className="w-4 h-4" />
              </div>
              <p className="text-xs font-bold text-slate-700 dark:text-slate-300">
                This note is locked
              </p>
              <p className="text-[10px] text-slate-400">
                Click anywhere on this card to enter PIN
              </p>
            </div>
          ) : (
            <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-300 line-clamp-4 leading-relaxed whitespace-pre-wrap font-sans">
              {note.content || 'No description provided.'}
            </p>
          )}
        </div>
      </div>

      {/* Footer: Date & Indicators */}
      <div className="pt-3 border-t border-slate-100 dark:border-slate-800/80 mt-auto flex items-center justify-between gap-2 text-[11px]">
        <span className="text-[11px] text-slate-400 font-mono">
          {note.updatedAt ? new Date(note.updatedAt).toLocaleDateString() : 'Recent'}
        </span>

        {note.isLocked && (
          <span className="text-[10px] font-bold text-amber-600 dark:text-amber-400 flex items-center gap-1">
            <Lock className="w-3 h-3" /> PIN Protected
          </span>
        )}
      </div>
    </div>
  );
};
